import { TaskFieldsTableLayout } from "@jugl-web/domain-resources/tasks/components/TaskFieldsTableLayout";

import {
  useTranslations,
  convertSnakeCaseToReadableString,
} from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { FormField } from "@jugl-web/rest-api";
import { Tooltip } from "@jugl-web/ui-kit";
import { useCustomerFields } from "@jugl-web/domain-resources/customers/hooks/useCustomerFields";
import { DndContext, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { CUSTOMER_FIELDS_TABLE_DIMENSIONS } from "./consts";
import { CustomerFieldRows } from "./components/CustomerFieldRows";
import { ReactComponent as TipIcon } from "./assets/tip.svg";

const { Container, Row, Cell, HeadingCellText } = TaskFieldsTableLayout;

interface CustomerFieldsTableProps {
  entityId: string;
  searchQuery: string;
  variant: FormField["section"];
  openManageFieldSidebar: (field: FormField) => void;
}

export const CustomerFieldsTable: FC<CustomerFieldsTableProps> = ({
  entityId,
  searchQuery,
  variant,
  openManageFieldSidebar,
}) => {
  const { t } = useTranslations();
  const { formFields } = useCustomerFields({
    entityId,
  });

  const emptySearchQuery = useMemo(() => {
    return formFields?.every(
      (field) =>
        !convertSnakeCaseToReadableString(field.name)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
    );
  }, [formFields, searchQuery]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const sensors = useSensors(mouseSensor);

  return (
    <>
      <Container>
        <Row
          height={CUSTOMER_FIELDS_TABLE_DIMENSIONS.HEADING_ROW_HEIGHT}
          isFirst
          className="font-secondary"
        >
          <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.REORDER_CELL_WIDTH} />
          <Cell
            width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.NAME_CELL_WIDTH}
            className="grow"
          >
            <HeadingCellText>
              {t({
                id: "customers-page.field-name-table-header",
                defaultMessage: "Field Name",
              })}
            </HeadingCellText>
          </Cell>
          <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.STANDARD_CELL_WIDTH}>
            <HeadingCellText>
              {t({
                id: "customers-page.field-type",
                defaultMessage: "Field type",
              })}
            </HeadingCellText>
          </Cell>
          <Cell
            width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.STANDARD_CELL_WIDTH}
            className="justify-between"
          >
            <HeadingCellText>
              {t({
                id: "customers-page.values-table-header",
                defaultMessage: "Values",
              })}
            </HeadingCellText>
          </Cell>
          <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.VISIBILITY_CELL_WIDTH}>
            <HeadingCellText className="flex w-full items-center justify-between">
              {t({
                id: "customers-page.visible-for-everyone",
                defaultMessage: "Visible for everyone",
              })}
              <Tooltip
                renderTrigger={({ ref, props }) => (
                  <span
                    ref={ref}
                    {...props}
                    className="ml-1 inline-block cursor-pointer"
                  >
                    <TipIcon />
                  </span>
                )}
              >
                {t({
                  id: "customers-page.visible-for-everyone-tooltip",
                  defaultMessage:
                    'Define what Customer Attributes will be visible for Everyone in this Workspace ℹ️ If Attribute status is "Not visible" - only Admins of the Workspace will be able to see it.',
                })}
              </Tooltip>
            </HeadingCellText>
          </Cell>
          <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.MANDATORY_CELL_WIDTH}>
            <HeadingCellText>
              {t({
                id: "customers-page.mandatory-table-header",
                defaultMessage: "Mandatory",
              })}
            </HeadingCellText>
          </Cell>

          <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.STANDARD_CELL_WIDTH}>
            <HeadingCellText>
              {t({
                id: "customers-page.creator",
                defaultMessage: "Creator",
              })}
            </HeadingCellText>
          </Cell>

          <Cell
            width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.ACTIONS_CELL_WIDTH}
            shouldGrow
          />
        </Row>
        <DndContext sensors={sensors}>
          <CustomerFieldRows
            entityId={entityId}
            searchQuery={searchQuery}
            variant={variant}
            openManageFieldSidebar={openManageFieldSidebar}
          />
        </DndContext>
      </Container>
      {emptySearchQuery && (
        <div className="mt-[80px] flex items-center justify-center bg-white font-secondary text-sm text-dark-700">
          {t({
            id: "customers-page.no-fields-matching-search",
            defaultMessage: "No Results 😔.",
          })}
        </div>
      )}
    </>
  );
};
